import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface DropdownItem {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'primary' | 'success';
  disabled?: boolean;
  separator?: boolean; // Ajoute un séparateur après cet item
}

interface DropdownProps {
  items: DropdownItem[];
  trigger?: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
}

interface DropdownPosition {
  top: number;
  left: number;
}

export function Dropdown({ items, trigger, align = 'right', className = '' }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Calculer la position du menu dropdown
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 192; // w-48 = 12rem = 192px
    const menuHeight = menuRef.current?.offsetHeight || 200;
    
    let top = triggerRect.bottom + 8; // mt-2 = 8px
    let left = align === 'right' 
      ? triggerRect.right - menuWidth 
      : triggerRect.left;
    
    // Vérifier si le menu dépasse en bas de l'écran
    if (top + menuHeight > window.innerHeight) {
      top = triggerRect.top - menuHeight - 8;
    }
    
    // Vérifier si le menu dépasse à droite de l'écran
    if (left + menuWidth > window.innerWidth) {
      left = window.innerWidth - menuWidth - 16;
    }
    
    // Vérifier si le menu dépasse à gauche de l'écran
    if (left < 16) {
      left = 16;
    }
    
    setPosition({ top, left });
  }, [align]);

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current && 
        !triggerRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        updatePosition();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Mettre à jour la position quand le menu s'ouvre
  useEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  const handleItemClick = (item: DropdownItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const getItemClasses = (variant?: string, disabled?: boolean) => {
    if (disabled) {
      return 'text-gray-400 cursor-not-allowed opacity-50';
    }
    
    switch (variant) {
      case 'danger':
        return 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20';
      case 'primary':
        return 'text-primary hover:bg-primary/10';
      case 'success':
        return 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20';
      default:
        return 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700';
    }
  };

  // Menu rendu via portal pour éviter les problèmes d'overflow
  const dropdownMenu = isOpen ? createPortal(
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
      }}
      className="w-48 rounded-lg shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 py-1 animate-in fade-in-0 zoom-in-95 duration-100"
    >
      {items.map((item, index) => (
        <div key={index}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleItemClick(item);
            }}
            disabled={item.disabled}
            className={`
              w-full flex items-center px-4 py-2.5 text-sm
              transition-colors
              ${getItemClasses(item.variant, item.disabled)}
              ${item.disabled ? '' : 'cursor-pointer'}
            `}
          >
            {item.icon && (
              <item.icon className="h-4 w-4 mr-3" />
            )}
            <span>{item.label}</span>
          </button>
          {item.separator && index < items.length - 1 && (
            <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
          )}
        </div>
      ))}
    </div>,
    document.body
  ) : null;

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
        aria-label="Actions"
        aria-expanded={isOpen}
      >
        {trigger || <MoreVertical className="h-4 w-4 text-gray-600 dark:text-gray-400" />}
      </button>

      {/* Dropdown menu via portal */}
      {dropdownMenu}
    </div>
  );
}
